/**
 * A Shopify install this browser has seen but the brand has not linked yet.
 *
 * The server cannot tell a brand about its own unclaimed install (the row has
 * no brand), so the only memory is this browser's. It holds the shop name and
 * a timestamp, never the claim token: the token stays single-use and in
 * memory, and the way back to a claim is a fresh, Shopify-signed app open.
 * The shop name only builds links to Shopify; nothing is claimed from it.
 */
const KEY = 'discovr-brand-shopify-unlinked';
const CHANGE_EVENT = 'discovr:shopify-unlinked-change';
const SHOP_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i;

const MAX_SKEW_MS = 60_000;

export interface UnlinkedShop {
  /** Absent when the install finished but no shop reached this browser. */
  shopDomain?: string;
  seenAt: number;
}

const isBrowser = (): boolean => typeof window !== 'undefined';

let memory: UnlinkedShop | undefined;
let cached: { raw: string | null; value: UnlinkedShop | undefined } | undefined;

function parse(raw: string | null): UnlinkedShop | undefined {
  if (!raw) return undefined;
  try {
    const v = JSON.parse(raw) as Partial<UnlinkedShop>;
    if (typeof v.seenAt !== 'number' || !Number.isFinite(v.seenAt)) return undefined;
    // A future timestamp would never expire.
    if (v.seenAt > Date.now() + MAX_SKEW_MS) return undefined;
    // A hand-edited value must not steer a link to some other site.
    if (v.shopDomain !== undefined && !SHOP_PATTERN.test(v.shopDomain)) return undefined;
    return { shopDomain: v.shopDomain, seenAt: v.seenAt };
  } catch {
    return undefined;
  }
}

function notify(): void {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Stable between calls while nothing changed, so it can back useSyncExternalStore. */
export function readUnlinkedShop(): UnlinkedShop | undefined {
  if (!isBrowser()) return memory;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return memory;
  }
  if (raw === null) {
    // Readable and empty: another tab dismissed or linked it.
    memory = undefined;
    cached = undefined;
    return undefined;
  }
  if (cached?.raw !== raw) cached = { raw, value: parse(raw) };
  return cached.value;
}

export function writeUnlinkedShop(shopDomain?: string): void {
  const valid = shopDomain && SHOP_PATTERN.test(shopDomain) ? shopDomain : undefined;
  const prev = readUnlinkedShop();
  // A sighting of a shop is a fresh claimable link, so it restarts the 24h clock;
  // a shop-less sighting adds no new link and keeps the earlier time.
  const next: UnlinkedShop = {
    shopDomain: valid ?? prev?.shopDomain,
    seenAt: valid === undefined && prev ? prev.seenAt : Date.now(),
  };
  memory = next;
  if (isBrowser()) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Memory copy still serves this page.
    }
  }
  notify();
}

export function clearUnlinkedShop(): void {
  memory = undefined;
  cached = undefined;
  if (isBrowser()) {
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      // ignore
    }
  }
  notify();
}

export function subscribeUnlinkedShop(onChange: () => void): () => void {
  if (!isBrowser()) return () => undefined;
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY || e.key === null) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}
